import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  IPmTypeAdminRow,
  useAdminPmTypeList,
  useAdminPmTypeUpdate,
} from '../../hooks/reactQuerys/ProcessManagerTypeAdminQueries';
import { useAlert } from '../../context/AlertContext';

interface ILocalEdit {
  ActiveFromHour: string;
  ActiveUntilHour: string;
  CooldownInHours: string;
  MaxTries: string;
}

const toInputValue = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

const parseHour = (raw: string): number | null | undefined => {
  // Rückgabe-Semantik:
  //   undefined → unverändert (Feld nicht angefasst)
  //   null      → explizit auf NULL setzen (Feld geleert)
  //   number    → gültige Stunde
  if (raw === '') return null;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return undefined;
  return n;
};

/**
 * Sperrzeiten-Editor pro ProcessManagerType — render-blockweise unter der
 * eigentlichen PM-Liste im DevHub. Pro Type:
 *   - ActiveFromHour / ActiveUntilHour (0..23, leer = unbeschränkt)
 *   - CooldownInHours (Mindest-Wartezeit zwischen Sweeps)
 *
 * Beide Werte gleich (z. B. 6/6) wirken als „24h offen" — die
 * Sweep-Logik im Backend interpretiert das absichtlich nicht als
 * Null-Fenster.
 */
export const PmTypeAdminSection: React.FC = () => {
  const { showAlert } = useAlert();
  const { pmTypes, isFetchingPmTypes, refetchPmTypes } = useAdminPmTypeList();
  const { updatePmType, isUpdatingPmType } = useAdminPmTypeUpdate();

  const [edits, setEdits] = useState<Record<number, ILocalEdit>>({});

  useEffect(() => {
    const initial: Record<number, ILocalEdit> = {};
    for (const t of pmTypes) {
      initial[t.idProcessManagerType] = {
        ActiveFromHour: toInputValue(t.ActiveFromHour),
        ActiveUntilHour: toInputValue(t.ActiveUntilHour),
        CooldownInHours: toInputValue(t.CooldownInHours),
        MaxTries: toInputValue(t.MaxTries),
      };
    }
    setEdits(initial);
  }, [pmTypes]);

  const handleChange = (id: number, field: keyof ILocalEdit, value: string) => {
    // Eingabe auf Ziffern + leer beschränken (für Stunden 0..23).
    const cleaned = value.replace(/[^0-9]/g, '').slice(0, 3);
    setEdits(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] ?? {
          ActiveFromHour: '',
          ActiveUntilHour: '',
          CooldownInHours: '',
          MaxTries: '',
        }),
        [field]: cleaned,
      },
    }));
  };

  const isDirty = (t: IPmTypeAdminRow): boolean => {
    const e = edits[t.idProcessManagerType];
    if (!e) return false;
    return (
      e.ActiveFromHour !== toInputValue(t.ActiveFromHour) ||
      e.ActiveUntilHour !== toInputValue(t.ActiveUntilHour) ||
      e.CooldownInHours !== toInputValue(t.CooldownInHours) ||
      e.MaxTries !== toInputValue(t.MaxTries)
    );
  };

  const handleSave = async (t: IPmTypeAdminRow) => {
    const e = edits[t.idProcessManagerType];
    if (!e) return;
    const from = parseHour(e.ActiveFromHour);
    const until = parseHour(e.ActiveUntilHour);
    if (from === undefined || until === undefined) {
      showAlert({
        text: 'Stunden müssen Zahlen zwischen 0 und 23 sein.',
        severity: 'warning',
      });
      return;
    }
    if (
      (from !== null && (from < 0 || from > 23)) ||
      (until !== null && (until < 0 || until > 23))
    ) {
      showAlert({
        text: 'Stunden müssen zwischen 0 und 23 liegen.',
        severity: 'warning',
      });
      return;
    }
    const cooldown =
      e.CooldownInHours === ''
        ? 0
        : Math.max(0, parseInt(e.CooldownInHours, 10) || 0);
    // Leer = unbegrenzt (NULL); sonst ganzzahlig >= 0.
    const maxTries =
      e.MaxTries === '' ? null : Math.max(0, parseInt(e.MaxTries, 10) || 0);

    try {
      await updatePmType({
        id: t.idProcessManagerType,
        body: {
          ActiveFromHour: from,
          ActiveUntilHour: until,
          CooldownInHours: cooldown,
          MaxTries: maxTries,
        },
      });
      showAlert({
        text: `Typ ${t.idProcessManagerType} aktualisiert.`,
        severity: 'success',
      });
    } catch (err) {
      showAlert({
        text:
          err instanceof Error
            ? `Update fehlgeschlagen: ${err.message}`
            : 'Update fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const describeWindow = (t: IPmTypeAdminRow): string => {
    const f = t.ActiveFromHour;
    const u = t.ActiveUntilHour;
    if (f === null || u === null || f === undefined || u === undefined) {
      return 'rund um die Uhr aktiv';
    }
    if (f === u) return 'rund um die Uhr aktiv';
    if (f < u)
      return `aktiv ${String(f).padStart(2, '0')}:00–${String(u).padStart(2, '0')}:00`;
    return `aktiv ${String(f).padStart(2, '0')}:00–24:00 + 00:00–${String(u).padStart(2, '0')}:00`;
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
        <Typography variant='h5'>Sperrzeiten je PM-Typ</Typography>
        <Button
          size='small'
          variant='outlined'
          onClick={() => refetchPmTypes()}
          disabled={isFetchingPmTypes}
        >
          Neu laden
        </Button>
      </Stack>
      <Alert severity='info' sx={{ mb: 1 }}>
        Sperrzeiten verhindern das Triggern bestimmter PM-Typen außerhalb der
        Geschäftszeiten. Werte zwischen 0–23 (Stunden, UTC). Beide leer oder
        gleich = rund um die Uhr aktiv. <strong>From &gt; Until</strong>{' '}
        bedeutet Wrap-Around über Mitternacht (z. B. 22→6 = nachts).
      </Alert>

      <Paper variant='outlined'>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell width={60}>ID</TableCell>
              <TableCell>Typ</TableCell>
              <TableCell width={120}>Active From</TableCell>
              <TableCell width={120}>Active Until</TableCell>
              <TableCell width={120}>Cooldown (h)</TableCell>
              <TableCell width={110}>Max. Versuche</TableCell>
              <TableCell width={260}>Aktuelles Fenster</TableCell>
              <TableCell width={140} align='right'>
                Aktion
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetchingPmTypes && pmTypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align='center'>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {pmTypes.map(t => {
              const e = edits[t.idProcessManagerType] ?? {
                ActiveFromHour: '',
                ActiveUntilHour: '',
                CooldownInHours: '',
                MaxTries: '',
              };
              const dirty = isDirty(t);
              return (
                <TableRow key={`pmt-${t.idProcessManagerType}`} hover>
                  <TableCell>
                    <Typography variant='body2' fontFamily='monospace'>
                      {t.idProcessManagerType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography variant='body2'>
                        {t.ProcessManagerType}
                      </Typography>
                      {t.isInstant === 1 && (
                        <Tooltip title='Wird sofort gesweept (ignoriert DateToCheck)'>
                          <Chip
                            size='small'
                            label='instant'
                            variant='outlined'
                          />
                        </Tooltip>
                      )}
                      {t.isVisible === 0 && (
                        <Chip
                          size='small'
                          label='unsichtbar'
                          variant='outlined'
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      placeholder='leer = ∞'
                      value={e.ActiveFromHour}
                      onChange={ev =>
                        handleChange(
                          t.idProcessManagerType,
                          'ActiveFromHour',
                          ev.target.value
                        )
                      }
                      inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      placeholder='leer = ∞'
                      value={e.ActiveUntilHour}
                      onChange={ev =>
                        handleChange(
                          t.idProcessManagerType,
                          'ActiveUntilHour',
                          ev.target.value
                        )
                      }
                      inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      value={e.CooldownInHours}
                      onChange={ev =>
                        handleChange(
                          t.idProcessManagerType,
                          'CooldownInHours',
                          ev.target.value
                        )
                      }
                      inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      placeholder='leer = ∞'
                      value={e.MaxTries}
                      onChange={ev =>
                        handleChange(
                          t.idProcessManagerType,
                          'MaxTries',
                          ev.target.value
                        )
                      }
                      inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant='caption' color='text.secondary'>
                      {describeWindow(t)}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Button
                      size='small'
                      variant={dirty ? 'contained' : 'outlined'}
                      onClick={() => handleSave(t)}
                      disabled={!dirty || isUpdatingPmType}
                    >
                      Speichern
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
